/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, StyleSheet, SafeAreaView, KeyboardAvoidingView, Platform, TouchableOpacity, ScrollView, Alert, Image, Animated, Linking } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { CountryPicker } from "react-native-country-codes-picker";
import * as Localization from 'expo-localization';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { whatsappSoporte } from '../constants/contacto';
import { useAppStore } from '../store/useAppStore';
import { validarTelefono, validarCorreo } from '../utils/telefono';
import { Ionicons } from '@expo/vector-icons';
import { authService } from '../services/authService';
import { getNativeAuth } from '../services/firebaseConfig';
import { firestoreService } from '../services/firestoreService';
import { LOGIN_ENFORCED } from '../constants/featureFlags';
import { authBridge, getCurrentBridgeUser } from '../services/authBridge';
import { volver } from '../utils/volver';
import {
  rutaSegunRol,
  verificacionAplazadaEnEstaSesion,
  aplazarVerificacionEnEstaSesion,
} from './_layout';
// ⚠️ IMPORTARLO EN ANDROID NO ROMPE NADA, y se comprobo antes de escribir esto.
// `ExpoAppleAuthenticationButton.js` envuelve el modulo nativo en un
// `if (Platform.OS === 'ios')` y en Android queda `undefined`, con lo que el
// componente devuelve null solo. Y `ExpoAppleAuthentication.js` usa
// `requireOptionalNativeModule` con un sustituto cuyo `isAvailableAsync()`
// responde false. La libreria esta escrita para no existir fuera de iOS.
import * as AppleAuthentication from 'expo-apple-authentication';
import { preguntarSiNo } from '../utils/preguntar';

// Mapa de países comunes para la autoselección rápida
const countryMap: Record<string, { code: string; flag: string }> = {
  'DO': { code: '+1', flag: '🇩🇴' },
  'US': { code: '+1', flag: '🇺🇸' },
  'MX': { code: '+52', flag: '🇲🇽' },
  'ES': { code: '+34', flag: '🇪🇸' },
  'CO': { code: '+57', flag: '🇨🇴' },
  'AR': { code: '+54', flag: '🇦🇷' },
  'CL': { code: '+56', flag: '🇨🇱' },
  'PE': { code: '+51', flag: '🇵🇪' },
  'VE': { code: '+58', flag: '🇻🇪' },
  'EC': { code: '+593', flag: '🇪🇨' },
  'PR': { code: '+1', flag: '🇵🇷' },
};

const PROMOTIONS = [
  // Decía "50% OFF en tu primer viaje" mientras la pantalla ANTERIOR promete
  // "¡Tu primer viaje es GRATIS!". Dos promesas distintas con un toque de
  // diferencia: el usuario no sabe cuál creer, y una de las dos es mentira.
  // Se unifica con lo que el código realmente hace (primer viaje cubierto).
  { text: "Tu primer viaje es GRATIS", icon: "gift" },
  // Este bono SÍ existe y SÍ llega. (Se quitó un rato por un análisis mío
  // equivocado: vi que `onSubscriptionActive` cuelga de la extensión de
  // suscripciones de Stripe, que está apagada, y di por muerto el camino. Pero
  // esa es UNA vía de seis: `contarReferidoPagado` se llama además desde
  // pagoCuotaStripe, recargaTarjeta, recargas, y tres sitios de loyalty —
  // tarjeta, recarga, depósito aprobado, cobro mensual, vencimiento y repaso.)
  // RD$800 en moto / RD$1,000 en carro por cada 3 invitados que paguen, y
  // `pagarBono` lo asienta en el acto, no el día 10.
  { text: "Bono por cada 3 choferes que invites y paguen", icon: "gift" },
  //
  // Y estas tres son las que de verdad nos separan del
  // resto, y las tres están medidas en el código:
  //
  //  · El precio lo acuerdan las dos partes (map.tsx sendRideOffer →
  //    rides.ts acceptRideOffer, que CONGELA `final_price`). Ninguna otra app
  //    de transporte en RD deja regatear, y aquí la gente ya regatea el pasaje.
  //  · El chofer cobra en mano (no hay payout ni escrow: payouts.ts). Lo que
  //    parecía una carencia es la razón de que la cuota sea fija y barata.
  //  · La verificación la hace una PERSONA y se puede nombrar documento por
  //    documento (kyc.ts + terms.tsx punto 4). Todas dicen "verificados";
  //    somos los únicos que podemos decir qué significa.
  { text: "Tú ofreces el precio, el chofer acepta", icon: "pricetag" },
  { text: "El chofer cobra en su mano, al momento", icon: "cash" },
  { text: "Cédula, licencia, matrícula y seguro: los revisa una persona", icon: "shield-checkmark" },
  // Decía "Viaja seguro con monitoreo 24/7". No hay monitoreo: nadie mira una
  // pantalla de viajes. Lo que sí existe y se puede señalar en el código es el
  // botón SOS (deja la alerta en `sos_alerts`, services/firestoreService.ts, y
  // el panel de admin la atiende — `SOS_ATTENDED` en functions/src/adminPanel.ts),
  // el PIN de inicio de viaje y la verificación del chofer, que la hace una
  // persona (terms.tsx punto 4). Se anuncia eso.
  { text: "Botón SOS y choferes verificados", icon: "shield-checkmark" },
  // "rápidos y baratos" no lo sostiene nada: la tarifa la pone el chofer y la
  // rapidez depende de que haya alguno cerca. Se deja solo lo que es cierto.
  { text: "Envía paquetes en moto o carro", icon: "cube" }
];

export default function AuthScreen() {
  const router = useRouter();
  const { isLogin: initialIsLogin } = useLocalSearchParams();
  const role = useAppStore((state) => state.userRole);
  
  const [isLogin, setIsLogin] = useState(initialIsLogin === 'true');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  // Campos de nombre — solo en signup. Antes faltaban y por eso el
  // perfil quedaba como "Usuario Nuevo" sin identidad real.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [referralInput, setReferralInput] = useState('');
  
  const [loading, setLoading] = useState(false);
  // ANTI-SECUESTRO DE CUENTA — contador de credenciales fallidas.
  // Firebase ya frena la fuerza bruta del lado servidor (too-many-requests),
  // pero eso llega tarde y sin explicación: el usuario sigue tecleando la
  // misma contraseña equivocada hasta quedar bloqueado. A los 3 intentos le
  // ofrecemos el camino correcto (recuperar por correo) en vez de dejarlo
  // insistir, que es justo el comportamiento que un atacante necesita.
  const failedAttemptsRef = useRef(0);
  const [showPicker, setShowPicker] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [countryFlag, setCountryFlag] = useState('🇩🇴');

  const [showPassword, setShowPassword] = useState(false);
  
  // Promociones animadas
  const [promoIndex, setPromoIndex] = useState(0);
  const [fadeAnim] = useState(() => new Animated.Value(1));

  useEffect(() => {
    const promoInterval = setInterval(() => {
      // Fade Out
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }).start(() => {
        // Cambiar índice
        setPromoIndex((prev) => (prev + 1) % PROMOTIONS.length);
        // Fade In
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }).start();
      });
    }, 4000);

    return () => clearInterval(promoInterval);
  }, [fadeAnim]);

  // Detectar automáticamente el país
  useEffect(() => {
    const region = Localization.getLocales()[0]?.regionCode;
    if (region && countryMap[region]) {
      setCountryCode(countryMap[region].code);
      setCountryFlag(countryMap[region].flag);
    }

    // RecaptchaVerifier es WEB-ONLY (Firebase Auth Phone) y requiere DOM.
    // Lo cargamos dinámicamente SOLO en web y NUNCA importamos el módulo
    // `firebase/auth` a nivel top-level — antes Hermes (RN/Android) cargaba
    // ese paquete y, aunque el código de RecaptchaVerifier no se ejecutaba,
    // los side effects del módulo web podían interferir con `@react-native-firebase/auth`.
    if (Platform.OS === 'web' && typeof window !== 'undefined' && !(window as any).recaptchaVerifier) {
      // Import dinámico — el bundler tree-shakea esto fuera del bundle nativo.
      import('firebase/auth').then(({ RecaptchaVerifier }) => {
        import('../services/firebaseConfig').then(({ auth: webAuth }) => {
          try {
            (window as any).recaptchaVerifier = new RecaptchaVerifier(webAuth, 'recaptcha-container', {
              size: 'invisible',
              callback: (_response: any) => { /* reCAPTCHA resuelto */ },
            });
          } catch (e) {
            console.warn('[auth] No se pudo montar RecaptchaVerifier:', e);
          }
        });
      });
    }
  }, []);

  const validatePassword = (pass: string) => {
    const hasLength = pass.length >= 8;
    const hasUpper = /[A-Z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    return { hasLength, hasUpper, hasNumber, hasSpecial, isValid: hasLength && hasUpper && hasNumber && hasSpecial };
  };

  // FIX BYPASS KYC: antes esta función hacía router.replace DIRECTO al
  // dashboard del rol post-login. Eso ganaba la race condition contra el
  // guard del _layout.tsx, que necesita 1-3s para consultar Firestore.
  // Resultado: el usuario entraba al mapa sin haber completado KYC.
  //
  // AHORA: consultamos /users/{uid} ANTES de navegar. Si kycCompleted es
  // false (o el doc no existe = usuario nuevo) → forzamos /kyc-welcome.
  // Si kyc OK → vamos al dashboard del rol. Timeout 6s para que el login
  // nunca quede colgado (si Firestore no responde, asumimos KYC pendiente,
  // más seguro que dejarlo entrar).
  // ── ¿SE PUEDE ENTRAR CON APPLE EN ESTE TELEFONO? ──────────────────────
  // Solo iOS 13 y superior. En Android y en web `appleDisponible()` devuelve
  // false sin tocar el modulo nativo, asi que el boton nunca se pinta y la
  // pantalla queda EXACTAMENTE como esta hoy en la APK.
  const [appleListo, setAppleListo] = useState(false);
  useEffect(() => {
    // ⚠️ EL CORTE VA AQUÍ, NO DENTRO DE `appleSignIn.ts`.
    //
    // Ese archivo protege cada llamada con `Platform.OS !== 'ios'`, y eso
    // basta para que no se toque el módulo nativo — pero su `import` de
    // `expo-apple-authentication` es ESTÁTICO y de nivel superior. En web
    // `import()` se baja el chunk (medido: 200 OK) y revienta al pedir un
    // módulo que el bundle web no trae, con un «Requiring unknown module» que
    // el reportero global apunta como FATAL. El guardia de dentro ni llega a
    // correr: el fallo es al CARGAR, no al llamar.
    //
    // Estaba así en producción (comprobado en vivo antes de tocar nada: el
    // mismo error con otro número de módulo, que cambia en cada
    // compilación). Android no se entera de esto: el botón de Apple solo
    // existe en iOS y allí el chunk sí tiene todo lo que pide.
    if (Platform.OS !== 'ios') return;
    let vivo = true;
    import('../services/appleSignIn')
      .then(({ appleDisponible }) => appleDisponible())
      .then((hay) => { if (vivo) setAppleListo(hay); })
      .catch(() => { /* sin Apple, se sigue con Google y correo */ });
    return () => { vivo = false; };
  }, []);

  /**
   * ENTRAR CON APPLE.
   *
   * Todo el peso esta en `services/appleSignIn.ts` (el nonce, el credential de
   * Firebase y el guardado del nombre, que Apple solo da una vez en la vida).
   * Aqui queda lo de la pantalla: el store local y a donde se navega.
   */
  const handleAppleLogin = async () => {
    try {
      const { entrarConApple } = await import('../services/appleSignIn');
      const r = await entrarConApple(useAppStore.getState().userRole ?? 'PASSENGER');

      // Cerro la hoja de Apple. Es una decision, no un fallo: ni una alerta.
      if (r.cancelado) return;

      if (r.nombre) {
        useAppStore.getState().setProfile(r.nombre, '', r.correo ?? '');
      }

      // ══ CON APPLE HACE AUN MAS FALTA QUE CON GOOGLE ══════════════════
      // Apple da el nombre SOLO en el primer inicio de sesion y el telefono
      // NUNCA. Y la persona puede esconder su nombre y su correo, asi que la
      // cuenta puede llegar sin nada con que rotularla — y el nombre es el que
      // el chofer lee para saber a quien recoge.
      // Mismo criterio que con Google: solo a quien acaba de crear su cuenta.
      if (r.cuentaNueva) {
        router.replace({ pathname: '/add-phone', params: { completar: '1' } } as any);
        return;
      }

      await navigateToRoleDashboard(r.uid);
    } catch (error: any) {
      const code = String(error?.code ?? '');
      const message = String(error?.message ?? '');
      console.warn('[AUTH] Apple Sign-In fallo —', code, message);

      // Mismo caso que con Google: ya hay cuenta con ese correo, creada antes
      // con correo + contrasena. El mensaje tiene que decir QUE HACER.
      if (
        code === 'auth/account-exists-with-different-credential' ||
        code === 'auth/duplicate-raw' ||
        message.includes('DUPLICATE_RAW_ID') ||
        message.includes('DUPLICATE_LOCAL_ID')
      ) {
        Alert.alert(
          'Cuenta duplicada',
          'Ya existe una cuenta con este correo electrónico. Probablemente la creaste antes con correo + contraseña.\n\n' +
            'Cierra esta ventana y entra con la opción "Ya tengo cuenta", usando tu correo y contraseña en vez de Apple.\n\n' +
            'Si no recuerdas la contraseña, toca "¿Olvidaste tu contraseña?" y te enviamos un enlace para cambiarla.'
        );
        return;
      }

      Alert.alert(
        'No pudimos entrar con Apple',
        'No pudimos completar el inicio con Apple en este momento.\n\nMientras tanto puedes entrar con tu correo y contraseña.',
        [
          { text: 'Entendido', style: 'cancel' },
          {
            text: 'Escribir a soporte',
            onPress: () => {
              Linking.openURL(
                whatsappSoporte(`Hola, no puedo entrar con Apple. Referencia: ${code || 'sin código'}`)
              ).catch(() => {});
            },
          },
        ]
      );
    }
  };

  const navigateToRoleDashboard = async (uid?: string | null) => {
    // Mira PRIMERO el bridge (native o REST). Si el signup cayó al fallback
    // REST, el SDK nativo no tiene currentUser pero el bridge sí.
    const bridgeUser = getCurrentBridgeUser();
    const passengerUid = uid ?? bridgeUser?.uid ?? getNativeAuth().currentUser?.uid;

    // ─── GATE DE CORREO VERIFICADO ────────────────────────────────────
    // Si el user vino por email/password y NO verificó el correo, lo
    // mandamos a /verify-email. Providers federados (Google/Apple) llegan
    // con emailVerified=true. El bridge funciona igual con users REST.
    if (bridgeUser?.email && !bridgeUser.emailVerified) {
      router.replace('/verify-email');
      return;
    }

    // Verificar KYC desde Firestore con timeout protector.
    let kycOk = false;
    // FIX RESTAURACIÓN DE ROL — Cuando el user reinstala la app, el store
    // local pierde userRole. Si miramos solo `role` del store, queda null →
    // el switch cae al default '/role-selection' y al user le aparecen TODOS
    // los perfiles como opciones, sintiéndose como si "todo está abierto".
    // Ahora restauramos role/baseRole desde Firestore (la fuente de verdad),
    // lo escribimos al store, y rotuemos al dashboard real del user.
    //
    // FIX INFERENCIA DE ROL — Para cuentas creadas ANTES de que el signup
    // persistiera role (cuentas legacy), Firestore no tiene profile.role.
    // Sin esto el user queda atrapado en el picker general aunque su KYC
    // esté listo. Inferimos el rol de OTROS campos que sí existen (los
    // flags y campos de KYC se setearon durante el flujo original) y lo
    // escribimos de vuelta a Firestore — la cuenta queda "migrada"
    // permanentemente al primer login y no vuelve a pasar por esta lógica.
    let effectiveRole: any = role;
    if (passengerUid) {
      try {
        const profileQuery = firestoreService.getUserProfile(passengerUid);
        const timeoutQuery = new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), 6000)
        );
        const profile: any = await Promise.race([profileQuery, timeoutQuery]);
        kycOk = !!(profile?.kycCompleted ?? profile?.isVerified);
        let persistedRole = profile?.role ?? null;
        let persistedBaseRole = profile?.baseRole ?? persistedRole;
        let inferred = false;
        if (!persistedRole && profile) {
          // Inferencia de rol desde campos existentes.
          if (profile.schoolName || profile.schoolId === passengerUid) {
            persistedRole = 'SCHOOL_DIRECTOR';
          } else if (profile.vehicleType === 'SCHOOL_BUS') {
            persistedRole = 'SCHOOL_DRIVER';
          } else if (profile.isDriverVerified === true || profile.vehicleType) {
            persistedRole = 'DRIVER';
          } else if (profile.restaurantOwnerOf || profile.restaurantId) {
            persistedRole = 'RESTAURANT_OWNER';
          } else if (kycOk) {
            // Tiene KYC biométrico pero sin pistas de chofer/director/restaurante:
            // muy probable PASSENGER (caso por defecto del flujo original).
            persistedRole = 'PASSENGER';
          }
          persistedBaseRole = persistedRole;
          inferred = !!persistedRole;
        }
        if (persistedRole) {
          useAppStore.getState().setRole(persistedRole);
          useAppStore.getState().setBaseRole(persistedBaseRole);
          effectiveRole = persistedRole;

          // FIX RESTAURACIÓN PERFIL — Antes solo se restauraba el rol,
          // pero el nombre/teléfono/email/foto del KYC también viven en
          // Firestore. Sin esto, cada reinstall:
          //   - Nombre caía al "userName" derivado del email (ej. "santo")
          //   - Foto de perfil quedaba en blanco aunque el KYC haya
          //     guardado la selfie en /users/{uid}.profilePhoto
          //   - Teléfono volvía vacío
          // Ahora trasladamos todo el perfil al store local al loguearse,
          // así el primer render de cualquier pantalla ya muestra los
          // datos verificados de la cédula y la selfie biométrica.
          try {
            const profileFirst = String(profile?.first_name ?? '').trim();
            const profileLast = String(profile?.last_name ?? '').trim();
            const profileDisplay = String(profile?.displayName ?? '').trim();
            const finalName =
              profileDisplay ||
              `${profileFirst} ${profileLast}`.trim() ||
              useAppStore.getState().userName || '';
            const finalPhone = String(profile?.phone ?? useAppStore.getState().userPhone ?? '').trim();
            const finalEmail =
              String(profile?.email ?? bridgeUser?.email ?? useAppStore.getState().userEmail ?? '').trim();
            if (finalName || finalPhone || finalEmail) {
              useAppStore.getState().setProfile(finalName, finalPhone, finalEmail);
            }
            const profilePhoto = String(profile?.profilePhoto ?? '').trim();
            if (profilePhoto) {
              useAppStore.getState().setUserPhoto(profilePhoto);
              useAppStore.getState().setProfilePhotoTaken(true);
            }
          } catch (e) {
            console.warn('[AUTH] restore profile data falló:', (e as any)?.message);
          }

          // NOTA: aquí había una llamada a Railway para PERSISTIR el rol
          // inferido de las cuentas legacy. Se eliminó por dos razones:
          //   1. Railway está borrado — la llamada siempre daba 404 y el
          //      error se tragaba con un .catch(), así que nunca migró nada.
          //   2. `role` y `baseRole` están bloqueados al cliente en
          //      firestore.rules a propósito (nadie se auto-asigna rol), así
          //      que tampoco se puede escribir directo desde aquí.
          // No hace falta: la inferencia de arriba corre en cada login sobre
          // datos que YA tenemos en mano — son unas comparaciones de texto,
          // sin lecturas extra. Persistirlo era una optimización, no un
          // requisito. Si algún día conviene, va en una Cloud Function.
          if (inferred) {
            console.warn('[AUTH] rol inferido para cuenta legacy:', persistedRole);
          }
        }
      } catch {
        kycOk = false; // si falla → asumimos KYC pendiente (más seguro)
      }
    }

    // KYC pendiente → ir al embudo de verificación (escudo verde).
    //
    // Antes había aquí una excepción que mandaba a SCHOOL_DIRECTOR a
    // `/kyc-director`. Con el perfil escolar anulado (5-oct-2026) esa pantalla
    // tiene candado y rebota, así que mandar a alguien ahí era mandarlo a un
    // rebote. Quien tenga ese rol guardado entra por el KYC normal como
    // cualquier otro usuario.
    if (!kycOk) {
      // FIX AUDIT — RENT_CAR_OWNER ya NO se salta el KYC. Antes caía directo al
      // dashboard sin kycCompleted, y las reglas de /vehicles y
      // /rent_negotiations lo bloqueaban (no podía crear carros ni chatear).
      // Ahora cae al flujo KYC estándar (kyc-welcome) como el resto.
      if (effectiveRole === undefined || effectiveRole === null) {
        // Sin rol elegido → primero role-selection
        router.replace('/role-selection');
        return;
      }
      // UNA SESION, UN RECORDATORIO. Si ya se le enseno el embudo y dijo
      // «Ahora no», no se le vuelve a poner delante nada mas entrar: va a su
      // pantalla. Sin esto, entrar caia SIEMPRE aqui — que es justo lo que el
      // fundador reporto el 17-oct-2026: «entra con el correo y se queda
      // anclado en la pantalla de verificacion».
      if (verificacionAplazadaEnEstaSesion()) {
        router.replace(rutaSegunRol(effectiveRole as any) as any);
        return;
      }
      aplazarVerificacionEnEstaSesion();
      router.replace('/kyc-welcome');
      return;
    }

    // TELÉFONO OBLIGATORIO — Los usuarios que entran con Google (u otro flujo
    // sin teléfono) no tienen número en el perfil. Sin él, chofer y pasajero
    // no pueden contactarse, así que lo exigimos ANTES de aterrizar en el
    // dashboard. Los usuarios de correo ya lo pusieron en el signup, por lo
    // que su userPhone está lleno y no pasan por aquí.
    const phoneDigits = String(useAppStore.getState().userPhone ?? '').replace(/[^0-9]/g, '');
    if (phoneDigits.length < 8) {
      router.replace('/add-phone');
      return;
    }

    // KYC OK → landing del rol.
    switch (effectiveRole) {
      case 'PASSENGER':
        router.replace('/(tabs)/map');
        return;
      case 'RENT_CAR_OWNER':
        router.replace('/(tabs)/dashboard');
        return;
      case 'CAR_RENTER':
        router.replace('/(tabs)/marketplace');
        return;
      case 'SCHOOL_DIRECTOR':
        router.replace('/school-director-dashboard');
        return;
      case 'RESTAURANT_OWNER':
        router.replace('/restaurant-dashboard');
        return;
      case 'FOOD_DELIVERY':
        router.replace('/(tabs)/food');
        return;
      case 'DRIVER':
      case 'SCHOOL_DRIVER':
      case 'DELIVERY_DRIVER':
        router.replace('/(tabs)/map');
        return;
      default:
        router.replace('/role-selection');
    }
  };

  const handleAuth = async () => {
    // 1. Restaurar la Captura de Datos: los inputs están mapeados correctamente a sus estados.
    // Sanitización y Validación estricta
    // Se usa el validador de la casa (src/utils/telefono.ts) en vez del regex
    // que estaba copiado aqui: ademas de la forma, avisa cuando el dominio esta
    // a un tecleo de uno de los grandes.
    const correoEscrito = validarCorreo(email);
    if (!correoEscrito.ok) {
      Alert.alert('Revisa tu correo', correoEscrito.motivo);
      return;
    }

    // UN DOMINIO MAL ESCRITO NO LO CAZA NINGUN REGEX: `gmil.com` es una
    // direccion valida de un dominio que no existe. Al REGISTRARSE eso deja a
    // la persona atrapada para siempre en "Verifica tu correo", porque el
    // enlace no le llega nunca. Paso de verdad el 15-oct-2026, con la campana
    // de anuncios corriendo: ese registro ya estaba pagado.
    // Solo al registrarse: al ENTRAR, un correo mal escrito simplemente no
    // encuentra la cuenta y se ve el error de siempre, que ya se entiende.
    if (!isLogin && correoEscrito.quisoDecir) {
      const corregir = await preguntarSiNo(
        'Revisa tu correo',
        `Escribiste ${correoEscrito.correo}.

¿Quisiste decir ${correoEscrito.quisoDecir}? ` +
          'Si está mal escrito, el enlace de verificación no te va a llegar y no vas a poder entrar.',
        'Está bien así',
        `Sí, corrígelo`
      );
      if (corregir) {
        setEmail(correoEscrito.quisoDecir);
        Alert.alert('Listo', 'Te lo corregí. Toca Continuar otra vez.');
        return;
      }
    }
    
    if (!isLogin) {
      // SEGURIDAD — Ya NO validamos nombre/apellido: el nombre lo escribe el
      // admin que aprueba el KYC leyéndolo de la cédula (`nombreLegal` en
      // `reviewKycSubmission`, functions/src/kyc.ts). NO hay OCR — ese camino
      // se eliminó. Solo exigimos email, teléfono y contraseña al registrarse.
      // La CUARTA puerta del teléfono, y la que se quedó fuera cuando se
      // centralizó el criterio en `src/utils/telefono.ts`. Aquí seguía la
      // comprobación vieja de solo longitud, así que `0000000000` entraba por
      // el registro aunque ya no entrara por ninguna otra parte.
      //
      // ⚠️ Se cambia SOLO la comprobación. Las líneas de más abajo que arman
      // `${countryCode}${phone.replace(...)}` se quedan como están: viven
      // fuera de este bloque `if (!isLogin)` y usar aquí una variable nueva
      // las dejaría fuera de alcance — rompería el login.
      const telRegistro = validarTelefono(phone);
      if (!telRegistro.ok) {
        Alert.alert('Revisa el número', telRegistro.motivo);
        return;
      }
      const sanitizedPhone = telRegistro.digitos;

      const { isValid } = validatePassword(password);
      if (!isValid) {
        Alert.alert("Error", "La contraseña no cumple con los requisitos de seguridad mínimos.");
        return;
      }
    } else {
      if (password.length === 0) {
        Alert.alert("Error", "Por favor ingresa tu contraseña para acceder.");
        return;
      }
    }
    
    setLoading(true);
    try {
      if (email.length > 0) {
        // authBridge intenta primero el SDK nativo con timeout 8 s; si el
        // SDK nativo se cuelga (Play Integrity sin token), cae al cliente
        // REST de Firebase. Cualquiera de los dos canales devuelve un
        // BridgeUser con uid/email/emailVerified. El resto de la app
        // (verify-email, _layout observer) ya está adaptado para leer
        // del bridge en ambos casos.
        console.warn('[AUTH] iniciando handleAuth, isLogin =', isLogin);
        // Código de invitación escrito a mano (instalación fresca sin deep link).
        // Lo guardamos como pendiente ANTES de crear la cuenta; se canjea solo
        // cuando la sesión esté confirmada (referralService.applyPendingIfAny en
        // el bootstrap de _layout). El typed gana sobre un posible deep link.
        if (!isLogin && referralInput.trim()) {
          const { referralService } = await import('../services/referralService');
          await referralService.setPending(referralInput);
        }

        const bridgeUser = isLogin
          ? await authBridge.signIn(email, password)
          : await authBridge.signUp(email, password);
        console.warn('[AUTH] ✅ bridge respondió, source =', bridgeUser.source, 'uid =', bridgeUser.uid);
        failedAttemptsRef.current = 0;   // entró bien: se limpia el contador

        // ─── RED DE SEGURIDAD DEL DOC /users/{uid} ───────────────────────
        // La creación del perfil dependía SOLO del backend en Railway
        // (POST /api/users/profile-setup, fire-and-forget). Si Railway está
        // caído o lento — verificado: hoy TODOS sus endpoints devuelven 404 —
        // el doc nunca se crea y nadie se entera.
        //
        // Sin ese doc:
        //   · getUserProfile() → null → el guard deja al user atrapado en KYC
        //   · las reglas de Firestore hacen get() sobre /users/{uid} dentro de
        //     myRole()/hasRole() → el get falla → DENIEGAN en cascada →
        //     pantallas vacías → "la app no carga" (rechazo vc16 de Google)
        //
        // ensureUserProfile lo crea desde el cliente con el SDK nativo. Es
        // idempotente (si ya existe no toca nada) y corre TAMBIÉN al iniciar
        // sesión, así cura las cuentas viejas que quedaron sin doc — incluida
        // la cuenta de prueba que se le entregó al revisor de Google Play.
        if (bridgeUser?.uid) {
          console.warn('[AUTH] asegurando doc /users/' + bridgeUser.uid);
          await firestoreService.ensureUserProfile(bridgeUser.uid, {
            email,
            phone: phone ? `${countryCode}${phone.replace(/[^0-9]/g, '')}` : null,
            name: useAppStore.getState().userName || null,
            role: useAppStore.getState().userRole ?? 'PASSENGER',
          });
        }

        setLoading(false);

        if (isLogin) {
          await navigateToRoleDashboard(bridgeUser.uid);
        } else {
          // SEGURIDAD — Ya NO capturamos nombre al registrarse. El nombre real
          // lo escribe a mano el admin al aprobar el KYC (`nombreLegal` en
          // `reviewKycSubmission`, functions/src/kyc.ts); NO hay OCR. Aquí solo
          // guardamos teléfono + email. El nombre queda vacío hasta que una
          // persona verifique el documento.
          const fullPhone = `${countryCode}${phone.replace(/[^0-9]/g, '')}`;
          useAppStore.getState().setProfile('', fullPhone, email);

          // Ya NO se llama al profile-setup de Railway (borrado, 404 — y el
          // fallo se tragaba con un .catch, así que el perfil nunca se creaba).
          // `ensureUserProfile` — llamado arriba, justo después del bridge —
          // ya escribió /users/{uid} con email, teléfono, nombre y rol usando
          // el SDK nativo de Firestore. Esta llamada era duplicada.

          // Forzar verificación de correo.
          router.replace('/verify-email');
        }
      } else if (phone.length > 0) {
        // Flujo de Teléfono SMS — SOLO web. En nativo el flujo necesita
        // @react-native-firebase/auth phoneNumber + reCAPTCHA Enterprise,
        // que NO está configurado en este proyecto. Mientras tanto:
        // mostramos un Alert y no movemos al user.
        if (Platform.OS === 'web') {
          // Import dinámico — mantiene firebase/auth fuera del bundle nativo.
          const { signInWithPhoneNumber } = await import('firebase/auth');
          const { auth: webAuth } = await import('../services/firebaseConfig');
          const appVerifier = (window as any).recaptchaVerifier;
          const fullPhoneNumber = `${countryCode}${phone.replace(/[^0-9]/g, '')}`;
          const confirmationResult = await signInWithPhoneNumber(webAuth, fullPhoneNumber, appVerifier);
          (window as any).confirmationResult = confirmationResult;
          setLoading(false);
          router.push('/verify-code');
        } else {
          // No decimos "Próximamente" — Google Play interpreta ese texto como
          // funcionalidad rota. Guiamos al usuario a los métodos que SÍ están
          // habilitados en móvil (correo y Google) sin sugerir bug.
          Alert.alert(
            'Continúa con correo o Google',
            'En la app móvil el registro se hace con correo electrónico o cuenta de Google (ambos disponibles arriba).'
          );
          setLoading(false);
        }
      }
    } catch (error: any) {
      setLoading(false);
      console.warn('[AUTH] ❌ ERROR:', error?.code, error?.message);
      const code = String(error?.code ?? '');
      let msg = 'No pudimos completar la autenticación. Intenta de nuevo en un momento.';

      if (code.includes('email-already-in-use')) {
        msg = 'Este correo ya está registrado. Inicia sesión en lugar de crear cuenta.';
      } else if (code.includes('invalid-email')) {
        msg = 'El correo no tiene un formato válido.';
      } else if (code.includes('weak-password')) {
        msg = 'La contraseña es muy débil. Usa mínimo 8 caracteres con mayúscula, número y símbolo.';
      } else if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
        // Mensaje genérico a propósito: decir cuál de los dos falló le
        // confirmaría a un atacante qué correos existen en la plataforma.
        failedAttemptsRef.current += 1;

        if (isLogin && failedAttemptsRef.current >= 3) {
          // Tercer fallo: dejamos de invitar a adivinar y ofrecemos el camino
          // seguro. Es también la señal que se le da al dueño legítimo de la
          // cuenta si alguien más está intentando entrar.
          failedAttemptsRef.current = 0;
          // ⚠️ Era `Alert.alert`: en react-native-web `Alert` es una función
          // vacía, así que quien quedó fuera no podía pedir el enlace de
          // recuperación desde el navegador. Ver utils/preguntar.ts.
          void preguntarSiNo(
            'Demasiados intentos fallidos',
            'Llevas 3 intentos con datos incorrectos. Por tu seguridad, no sigas adivinando: ' +
              'recupera el acceso con tu correo.\n\n' +
              'Si no fuiste tú quien intentó entrar, cambia tu contraseña de inmediato.',
            'Seguir intentando',
            'Enviarme enlace de recuperación'
          ).then((si: boolean) => { if (si) void handleForgotPassword(); });
          return;
        }

        const left = 3 - failedAttemptsRef.current;
        msg = isLogin
          ? `Correo o contraseña incorrectos. Te quedan ${left} intento(s) antes de que te sugiramos recuperar la cuenta.`
          : 'Correo o contraseña incorrectos. Verifica tus datos.';
      } else if (code.includes('user-disabled')) {
        msg = 'Esta cuenta fue desactivada. Contacta soporte.';
      } else if (code.includes('too-many-requests')) {
        msg = 'Demasiados intentos. Espera unos minutos antes de volver a intentar.';
      } else if (code === 'auth/network-slow') {
        // Timeout 25s en el REST de Firebase. La red SÍ funciona pero el
        // handshake con identitytoolkit.googleapis.com está tardando demasiado.
        msg = 'Tu conexión está tardando demasiado en responder. Cámbiate a Wi-Fi estable o espera unos segundos y vuelve a intentar — tus datos NO se perdieron.';
      } else if (code === 'auth/network-error' || code.includes('network-request-failed')) {
        msg = 'Sin conexión a internet. Verifica tu Wi-Fi o datos móviles.';
      }

      Alert.alert('Error', msg);
    }
  };

  // Google Sign-In nativo (real, sin mock). Top-level imports en el archivo
  // — antes había require() dinámico que en algunos builds de Hermes
  // resolvía las exportaciones distintas y la llamada a GoogleAuthProvider
  // fallaba con "is not supported on the native Firebase SDKs". Top-level
  // garantiza la API modular correcta.
  // FIX AUDIT — Antes el botón "¿Olvidaste tu contraseña?" no tenía onPress
  // (botón muerto, sin recuperación de cuenta). Ahora envía el correo de
  // restablecimiento de Firebase. Mensaje neutro para no revelar si el correo
  // existe (buena práctica de seguridad).
  const handleForgotPassword = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Alert.alert(
        'Escribe tu correo',
        'Ingresa tu correo en el campo de arriba y vuelve a tocar "¿Olvidaste tu contraseña?" para enviarte el enlace de recuperación.'
      );
      return;
    }
    try {
      if (Platform.OS === 'web') {
        const { sendPasswordResetEmail } = await import('firebase/auth');
        const { auth: webAuth } = await import('../services/firebaseConfig');
        await sendPasswordResetEmail(webAuth, email);
      } else {
        await getNativeAuth().sendPasswordResetEmail(email);
      }
    } catch (e: any) {
      console.warn('[auth] reset password:', e?.code || e?.message);
    }
    Alert.alert(
      'Revisa tu correo',
      `Si ${email} tiene una cuenta en Go Safely, te enviamos un enlace para restablecer tu contraseña. Revisa también tu carpeta de spam.`
    );
  };

  const handleGoogleLogin = async () => {
    /** ¿Esta cuenta de Google nació en ESTE login? Lo dice `ensureUserProfile`. */
    let cuentaRecienCreada = false;
    if (Platform.OS === 'web') {
      Alert.alert(
        'Google en preparación',
        'El inicio con Google en web requiere configuración OAuth adicional. Por ahora usa tu correo y contraseña.'
      );
      return;
    }
    try {
      // Imports nativos seguros — solo se evalúan al tocar el botón.
      const { GoogleSignin } = await import('@react-native-google-signin/google-signin');
      const { GoogleAuthProvider, signInWithCredential } = await import('@react-native-firebase/auth');

      await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
      // Forzar selector de cuenta cada vez (paridad con el web).
      try { await GoogleSignin.signOut(); } catch {}
      const signInResult: any = await GoogleSignin.signIn();
      const idToken: string | undefined =
        signInResult?.data?.idToken ?? signInResult?.idToken;
      if (!idToken) {
        throw new Error(
          'Google no devolvió idToken. Verifica EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID en .env.'
        );
      }

      // FIX WIFI FLAKY — signInWithCredential del SDK nativo se cuelga
      // SIN TIMEOUT cuando el Wi-Fi pierde paquetes en el handshake con
      // Identity Toolkit. Mobile data rara vez lo provoca. Síntoma: user
      // elige cuenta Google → pantalla no avanza → user piensa que la app
      // congeló y cierra forzado.
      //
      // Solución (espejo de authBridge.signIn/signUp): race nativo vs
      // timeout 3s. Si nativo no responde, llamamos al endpoint REST
      // signInWithIdp (que pasa por fetchWithRetry con 3 intentos y
      // backoff). Cualquiera de los dos canales nos da un user válido.
      const NATIVE_GOOGLE_TIMEOUT_MS = 3000;
      const credential = GoogleAuthProvider.credential(idToken);

      let googleResult: any = null;
      let googleResultEmail: string | null = null;
      let googleResultUid: string | null = null;
      let googleResultDisplayName: string | null = null;
      let googleResultPhoto: string | null = null;

      const nativePromise = signInWithCredential(getNativeAuth(), credential);
      const nativeRace = await Promise.race([
        nativePromise.then((res: any) => ({ kind: 'native' as const, res })),
        new Promise<{ kind: 'timeout' }>((resolve) =>
          setTimeout(() => resolve({ kind: 'timeout' }), NATIVE_GOOGLE_TIMEOUT_MS)
        ),
      ]);

      if (nativeRace.kind === 'native') {
        googleResult = nativeRace.res;
        const u = googleResult?.user;
        googleResultEmail = u?.email ?? null;
        googleResultUid = u?.uid ?? null;
        googleResultDisplayName = u?.displayName ?? null;
        googleResultPhoto = u?.photoURL ?? null;
      } else {
        // Nativo se colgó. Fallback REST con retry.
        console.warn(`[AUTH] signInWithCredential nativo no respondió en ${NATIVE_GOOGLE_TIMEOUT_MS}ms → fallback REST signInWithIdp`);
        const { authRest } = await import('../services/authRest');
        const { restAuthCache } = await import('../services/authBridge');
        const restUser = await authRest.signInWithIdp(idToken, 'google.com');
        await restAuthCache.set(restUser);
        // Useremail y demás vienen del REST. displayName/photoURL no los devuelve
        // signInWithIdp directo — los tomamos de signInResult de Google.
        googleResultEmail = restUser.email || null;
        googleResultUid = restUser.uid;
        googleResultDisplayName = signInResult?.data?.user?.name ?? null;
        googleResultPhoto = signInResult?.data?.user?.photo ?? null;
        // Estructura compat para el código de pre-fill abajo.
        googleResult = {
          user: {
            uid: restUser.uid,
            email: restUser.email,
            displayName: googleResultDisplayName,
            photoURL: googleResultPhoto,
          },
        };
        // Disparar exchangeToken para sesión nativa (Firestore/Storage),
        // fire-and-forget como hacemos en email/password.
        try {
          const { authBridge } = await import('../services/authBridge');
          // getIdToken devuelve el del cache REST recién seteado.
          const token = await authBridge.getIdToken();
          if (token) {
            // FIX BUG #2 (causa raíz #1) — ANTES esto SOLO hacía console.warn:
            // el comentario decía "Firestore funciona igual" pero es FALSO. Sin
            // el exchange, el usuario de Google queda sin sesión NATIVA
            // (getNativeAuth().currentUser = null) → request.auth == null →
            // TODA operación de viaje (crear/ofertar/aceptar) es rechazada en
            // silencio por las reglas. Ahora SÍ disparamos el exchange (con
            // retry interno), igual que el login por correo.
            authBridge.ensureNativeSession(token);
          }
        } catch (exErr) {
          console.warn('[AUTH] Google REST: exchange-token init falló (no crítico):', exErr);
        }
      }

      // FIX módulo 5 — Pre-llenar el perfil con los datos que Google nos
      // entregó (displayName, email, photoURL). Antes el user entraba con
      // Google pero su /users/{uid} quedaba sin nombre/avatar y el perfil
      // aparecía "Usuario Nuevo".
      try {
        const gUser = googleResult?.user;
        const displayName = String(gUser?.displayName ?? signInResult?.data?.user?.name ?? '').trim();
        const photoUrl = String(gUser?.photoURL ?? signInResult?.data?.user?.photo ?? '');
        const parts = displayName.split(/\s+/).filter(Boolean);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';
        const userEmail = String(gUser?.email ?? signInResult?.data?.user?.email ?? '');

        // Store local de inmediato (sin esperar backend).
        if (displayName) {
          useAppStore.getState().setProfile(displayName, '', userEmail);
        }
        // FIX FOTO KYC — Antes seteábamos la foto de Google aunque el user
        // YA tuviera selfie biométrica del KYC. La foto genérica del email
        // aplastaba la real. Ahora la foto de Google es solo un PLACEHOLDER
        // mientras el KYC se completa: si no hay foto previa en el store,
        // la mostramos; si hay (KYC ya hecho), la respetamos. Y NO la
        // enviamos como avatarUrl al backend para no pisar profilePhoto
        // en /users/{uid}. La fuente única autoritaria del avatar es el
        // KYC biométrico (kyc-biometrics.tsx → uploadProfilePhoto).
        const hasExistingPhoto = !!useAppStore.getState().userPhoto;
        if (photoUrl && !hasExistingPhoto) {
          useAppStore.getState().setUserPhoto(photoUrl);
        }

        // Persistir en /users/{uid} vía backend. Fire-and-forget.
        // FIX RESTAURACIÓN — enviamos role + baseRole para que reinstalls +
        // login restauren el perfil. NO enviamos avatarUrl: el KYC manda.
        // Igual que en el registro por correo: la persistencia del perfil ya
        // la hace `ensureUserProfile` (abajo, con el uid de Google) contra
        // Firestore nativo. La llamada a Railway que había aquí estaba muerta.
      } catch (prefillErr) {
        console.warn('[AUTH] pre-fill desde Google falló (no crítico):', prefillErr);
      }

      // RED DE SEGURIDAD — igual que en el login por correo. Este camino
      // dependía SOLO del fetch fire-and-forget a Railway (línea 721), que
      // hoy responde 404 en todas sus rutas. Sin doc en /users/{uid} las
      // reglas de Firestore deniegan en cascada y la app se ve rota.
      // Un revisor de Google entra justamente por aquí para no dar su correo
      // personal, así que este era el camino MÁS probable del rechazo.
      const googleUid = googleResult?.user?.uid;
      if (googleUid) {
        cuentaRecienCreada = await firestoreService.ensureUserProfile(googleUid, {
          email: googleResult?.user?.email ?? null,
          name: googleResult?.user?.displayName ?? null,
          role: useAppStore.getState().userRole ?? 'PASSENGER',
        });
      }

      // ══ QUIEN ACABA DE CREAR SU CUENTA CON GOOGLE, COMPLETA SU PERFIL ══
      //
      // Google trae el nombre y la foto de la cuenta de Google y la app los
      // daba por buenos. Pero un nombre de Google puede ser una MARCA: el
      // fundador vio una cuenta llamada «farandula y humor», con el logo de
      // un canal, y sin forma de cambiarlo. Y Google no trae telefono.
      //
      // Quien se registra por el camino normal SI escribe sus datos; el de
      // Google se los salta. Esta es la pantalla que le faltaba.
      //
      // SOLO A LAS CUENTAS NUEVAS, y es deliberado: pedirselo tambien a quien
      // ya esta en la calle seria interrumpir a casi todo el mundo —el
      // registro por correo nunca pidio el nombre— para cobrarle algo que la
      // app jamas le pidio. Al que ya esta dentro el mapa le sigue pidiendo el
      // telefono cuando va a pedir un viaje, como hasta hoy.
      //
      // El rol NO se pregunta aqui: al guardar, add-phone manda al selector de
      // perfil si todavia no eligio uno. Y si elige chofer, role-selection lo
      // lleva derecho a verificarse, que es lo que pidio el fundador.
      if (cuentaRecienCreada) {
        router.replace({ pathname: '/add-phone', params: { completar: '1' } } as any);
        return;
      }

      await navigateToRoleDashboard(googleUid);
    } catch (error: any) {
      const code = String(error?.code ?? '');
      const message = String(error?.message ?? '');

      // SIGN_IN_CANCELLED → user voluntariamente cerró el picker. Silencio.
      if (
        code === 'SIGN_IN_CANCELLED' ||
        code === '12501' ||
        code.includes('SIGN_IN_CANCELLED')
      ) return;

      // QA brief: mostrar SIEMPRE el código y mensaje exactos del SDK
      // nativo para que el desarrollador pueda copiarlos a issue/log.
      // Debajo, hint contextual según el código conocido.
      //
      // FIX FRIENDLY ERROR — DUPLICATE_RAW_ID y account-exists-with-different-credential
      // son los dos casos donde el user ve una pantalla técnica horrible
      // y no entiende qué hacer. Antes solo se mostraba "[auth/duplicate-raw]
      // An internal error has occurred. [ DUPLICATE_RAW_ID ]" — ahora le
      // damos un mensaje humano + qué hacer.
      const isDuplicateAccount =
        code === 'auth/duplicate-raw' ||
        code === 'auth/account-exists-with-different-credential' ||
        message.includes('DUPLICATE_RAW_ID') ||
        message.includes('DUPLICATE_LOCAL_ID');

      if (isDuplicateAccount) {
        Alert.alert(
          'Cuenta duplicada',
          'Ya existe una cuenta con este correo electrónico. Probablemente la creaste antes con correo + contraseña.\n\n' +
            // Estaba escrito en voseo argentino ("Cerrá", "entrá", "acordás",
            // "escribinos"). En República Dominicana se habla de tú.
            'Cierra esta ventana y entra con la opción "Ya tengo cuenta", usando tu correo y contraseña en vez de Google.\n\n' +
            'Si no recuerdas la contraseña, toca "¿Olvidaste tu contraseña?" y te enviamos un enlace para cambiarla.'
        );
        return;
      }

      // MENSAJE PARA PERSONAS, NO PARA PROGRAMADORES.
      // Antes esto mostraba un volcado técnico: "Código nativo: RNGoogleSignin /
      // Mensaje: apiClient is null - call configure() first". El detalle
      // técnico sigue existiendo, pero va al log del dispositivo — que es donde
      // sirve. Al usuario se le dice qué pasó y qué puede hacer, y siempre se
      // le deja una salida: entrar con correo y contraseña.
      console.warn('[AUTH] Google Sign-In falló —', code, message);

      let explicacion = 'No pudimos conectarte con Google en este momento.';
      if (code === 'NETWORK_ERROR' || code === '7' || message.toLowerCase().includes('network')) {
        explicacion = 'No hay conexión estable con Google. Revisa tu Wi-Fi o tus datos móviles.';
      } else if (code === 'SIGN_IN_REQUIRED' || code === '4') {
        explicacion = 'Tu teléfono no tiene una cuenta de Google activa. Agrégala desde Ajustes → Cuentas y vuelve a intentar.';
      } else if (code === 'INTERNAL_ERROR' || code === '8') {
        explicacion = 'Los servicios de Google en tu teléfono no respondieron. Reinicia el teléfono e intenta de nuevo.';
      }

      Alert.alert(
        'No pudimos entrar con Google',
        `${explicacion}\n\nMientras tanto puedes entrar con tu correo y contraseña.`,
        [
          { text: 'Entendido', style: 'cancel' },
          {
            text: 'Escribir a soporte',
            onPress: () => {
              Linking.openURL(
                whatsappSoporte(`Hola, no puedo entrar con Google. Referencia: ${code || 'sin código'}`)
              ).catch(() => {});
            },
          },
        ]
      );
    }
  };

  const passValidation = validatePassword(password);

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <TouchableOpacity 
          onPress={() => {
            if (router.canGoBack()) {
              volver(router);
            } else {
              router.replace('/');
            }
          }} 
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color={Colors.white} />
        </TouchableOpacity>

        <View style={styles.header}>
          <Text style={styles.logoText}>Go <Text style={styles.logoHighlight}>Safely</Text></Text>
          
          {/* BANNER DE PROMOCIONES ROTATIVAS */}
          <Animated.View style={[styles.promoPill, { opacity: fadeAnim }]}>
            <Ionicons name={PROMOTIONS[promoIndex].icon as any} size={14} color="#00C853" />
            <Text style={styles.promoText} numberOfLines={1}>
               {PROMOTIONS[promoIndex].text}
            </Text>
          </Animated.View>

          <Text style={styles.subtitle}>{isLogin ? "Bienvenido de nuevo" : "Crea tu cuenta segura"}</Text>
        </View>

        <View style={styles.form}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Opciones Sociales */}

            {/* ⚠️ APPLE VA PRIMERO, Y NO ES ESTETICA.
                La guideline 4.8 pide que la opcion que no comparte datos no
                quede en peor sitio que las otras. Ponerla arriba no deja lugar
                a interpretacion por parte de quien revise.

                Se usa el boton NATIVO de Apple (`AppleAuthenticationButton`) y
                no uno nuestro: viene ya aprobado, traducido y accesible, y
                Apple prohibe cambiarle el fondo o el radio con `style` — para
                eso estan `buttonStyle` y `cornerRadius`.

                En Android `appleListo` es false y esto no se pinta: la
                pantalla queda identica a la que ya esta en Play. */}
            {appleListo && (
              <AppleAuthentication.AppleAuthenticationButton
                buttonType={
                  isLogin
                    ? AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN
                    : AppleAuthentication.AppleAuthenticationButtonType.SIGN_UP
                }
                buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.BLACK}
                cornerRadius={SIZES.radius}
                style={styles.appleButton}
                onPress={handleAppleLogin}
              />
            )}

            <TouchableOpacity style={styles.googleButton} onPress={handleGoogleLogin}>
              <Image 
                source={{ uri: 'https://developers.google.com/identity/images/g-logo.png' }} 
                style={{ width: 22, height: 22, marginRight: 12 }} 
              />
              <Text style={styles.googleButtonText}>Continuar con Google</Text>
            </TouchableOpacity>

            <View style={styles.dividerContainer}>
              <View style={styles.divider} />
              <Text style={styles.dividerText}>o con tu correo</Text>
              <View style={styles.divider} />
            </View>

            {/* SEGURIDAD — Ya NO pedimos nombre al registrarse: el nombre y la
                foto del perfil salen de la verificación de identidad.
                ⚠️ AQUÍ NO HAY NADA AUTOMÁTICO, y el texto decía que sí ("se
                completarán automáticamente"). El OCR de la cédula se eliminó
                —lo dice kyc-biometrics.tsx: "Ya NO hay OCR de la cédula: el
                nombre real lo confirma la persona"— y quien escribe
                `nombreLegal` y sube la selfie a `photoURL` es el admin que
                aprueba en `reviewKycSubmission` (functions/src/kyc.ts). Es
                exactamente lo que prometen terms.tsx punto 4 y privacy.tsx
                punto 2.c: lo compara a mano una persona autorizada, sin
                reconocimiento facial ni sistema automático. */}
            {!isLogin && (
              <View style={styles.autoProfileNote}>
                <Ionicons name="shield-checkmark" size={18} color="#00C853" style={{ marginRight: 8 }} />
                {/* SE DICE LA REGLA, NO SOLO EL PROCEDIMIENTO.
                    El texto explicaba QUE pasa pero no QUE SE ESPERA de la
                    persona. Quien entra con una cuenta de Google llamada «El
                    Rapido» no tiene forma de saber que ese nombre no va a
                    quedar — y cuando se le cambie al aprobar, lo leera como un
                    fallo. Decision del fundador (30-sep-2026): decirlo antes. */}
                <Text style={styles.autoProfileNoteText}>
                  Cuando verifiques tu identidad, una persona de nuestro equipo confirma tu nombre con tu cédula y tu selfie pasa a ser tu foto de perfil.
                  Por seguridad de la plataforma no aceptamos apodos ni nombres distintos al de tu documento: el que quede será el de tu cédula.
                </Text>
              </View>
            )}

            <Text style={styles.label}>Correo Electrónico</Text>
            <View style={styles.inputContainer}>
              <Ionicons name="mail-outline" size={20} color={Colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                placeholder="ejemplo@correo.com"
                keyboardType="email-address"
                autoCapitalize="none"
                value={email}
                onChangeText={(text) => setEmail(text.trim())}
                placeholderTextColor={Colors.textSecondary}
                maxLength={60}
              />
            </View>

            {!isLogin && (
              <>
                <Text style={styles.label}>Número de Celular</Text>
                <View style={styles.inputContainer}>
                  <TouchableOpacity 
                    style={styles.countryPickerContainer}
                    onPress={() => setShowPicker(true)}
                  >
                    <Text style={styles.flag}>{countryFlag}</Text>
                    <Text style={styles.prefix}>{countryCode}</Text>
                    <Ionicons name="chevron-down" size={14} color={Colors.textSecondary} style={{ marginLeft: 4 }} />
                  </TouchableOpacity>

                  <TextInput
                    style={styles.input}
                    placeholder="(555) 000-0000"
                    keyboardType="phone-pad"
                    value={phone}
                    onChangeText={(text) => setPhone(text.replace(/[^0-9+() -]/g, ''))}
                    placeholderTextColor={Colors.textSecondary}
                    maxLength={18}
                  />
                </View>
              </>
            )}

            <Text style={styles.label}>Contraseña</Text>
            <View style={styles.inputContainer}>
              <Ionicons name="lock-closed-outline" size={20} color={Colors.textSecondary} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                placeholder="********"
                secureTextEntry={!showPassword}
                value={password}
                onChangeText={setPassword}
                placeholderTextColor={Colors.textSecondary}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={{ padding: 4 }}>
                <Ionicons name={showPassword ? "eye-off-outline" : "eye-outline"} size={20} color={Colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {!isLogin && password.length > 0 && (
              <View style={styles.validationContainer}>
                <Text style={styles.validationTitle}>Requisitos de la contraseña:</Text>
                <Text style={[styles.validationItem, passValidation.hasLength ? styles.valid : styles.invalid]}>
                  <Ionicons name={passValidation.hasLength ? "checkmark-circle" : "close-circle"} size={14} /> Mínimo 8 caracteres
                </Text>
                <Text style={[styles.validationItem, passValidation.hasUpper ? styles.valid : styles.invalid]}>
                  <Ionicons name={passValidation.hasUpper ? "checkmark-circle" : "close-circle"} size={14} /> Una letra mayúscula
                </Text>
                <Text style={[styles.validationItem, passValidation.hasNumber ? styles.valid : styles.invalid]}>
                  <Ionicons name={passValidation.hasNumber ? "checkmark-circle" : "close-circle"} size={14} /> Un número
                </Text>
                <Text style={[styles.validationItem, passValidation.hasSpecial ? styles.valid : styles.invalid]}>
                  <Ionicons name={passValidation.hasSpecial ? "checkmark-circle" : "close-circle"} size={14} /> Un carácter especial (@$!%*?&)
                </Text>
              </View>
            )}

            {!isLogin && (
              <>
                <Text style={styles.label}>Código de invitación (opcional)</Text>
                <View style={styles.inputContainer}>
                  <Ionicons name="gift-outline" size={20} color={Colors.textSecondary} style={styles.inputIcon} />
                  <TextInput
                    style={styles.input}
                    placeholder="Ej: ABZ4K9"
                    autoCapitalize="characters"
                    value={referralInput}
                    onChangeText={setReferralInput}
                    placeholderTextColor={Colors.textSecondary}
                  />
                </View>
              </>
            )}

            {isLogin && (
              <TouchableOpacity style={styles.forgotPassword} onPress={handleForgotPassword}>
                <Text style={styles.forgotPasswordText}>¿Olvidaste tu contraseña?</Text>
              </TouchableOpacity>
            )}
            
            <Button 
              title={isLogin ? "Iniciar Sesión" : "Crear Cuenta"} 
              onPress={handleAuth} 
              loading={loading}
              style={styles.button}
            />

            <TouchableOpacity style={styles.switchModeContainer} onPress={() => setIsLogin(!isLogin)}>
              <Text style={styles.switchModeText}>
                {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes una cuenta? "}
                <Text style={styles.switchModeLink}>{isLogin ? "Regístrate" : "Inicia Sesión"}</Text>
              </Text>
            </TouchableOpacity>
            
            {/* Antes era texto plano: se le pedía aceptar unos términos que no
                podía abrir, aunque las pantallas /terms y /privacy existen y
                están completas. Google Play exige que sean accesibles. */}
            <Text style={styles.terms}>
              Al continuar, aceptas nuestros{' '}
              <Text
                style={{ textDecorationLine: 'underline', fontWeight: '700' }}
                onPress={() => router.push('/terms')}
              >
                Términos de Servicio
              </Text>
              {' '}y la{' '}
              <Text
                style={{ textDecorationLine: 'underline', fontWeight: '700' }}
                onPress={() => router.push('/privacy')}
              >
                Política de Privacidad
              </Text>
              .
            </Text>
            
            {/* SALIDA SIN CUENTA — visible mientras el modo invitado esté abierto.
                Sin esto, cualquier acción bloqueada empuja al usuario a esta
                pantalla y la única vuelta es una flecha pequeña sobre el header
                de color. El revisor de Google lo lee como muro de registro
                obligatorio, que es exactamente lo que estamos evitando. */}
            {!LOGIN_ENFORCED && (
              <TouchableOpacity
                onPress={() => router.replace('/')}
                style={{ paddingVertical: 14, alignItems: 'center' }}
                accessibilityRole="button"
              >
                <Text style={{ color: Colors.accent, fontSize: 15, fontWeight: '600', textDecorationLine: 'underline' }}>
                  Seguir explorando sin cuenta
                </Text>
              </TouchableOpacity>
            )}

            <View style={styles.developerContainer}>
              <Text style={styles.developerText}>Desarrollado por SMR</Text>
            </View>
            
            <View id="recaptcha-container" />
            <View style={{height: 20}} />
          </ScrollView>
        </View>

        <CountryPicker
          show={showPicker}
          pickerButtonOnPress={(item) => {
            setCountryCode(item.dial_code);
            setCountryFlag(item.flag);
            setShowPicker(false);
          }}
          lang="es"
          onBackdropPress={() => setShowPicker(false)}
          style={{
            modal: {
              height: 500,
            },
          }}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  container: {
    flex: 1,
  },
  backButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 10 : 30,
    left: 20,
    zIndex: 10,
    padding: 8,
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 40 : 60,
    paddingBottom: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
  },
  logoText: {
    fontSize: 42,
    fontWeight: '900',
    color: Colors.white,
    letterSpacing: -1,
  },
  logoHighlight: {
    color: Colors.accent,
  },
  promoPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 200, 83, 0.15)', // Verde claro estilo 99/Didi
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginTop: 12,
    marginBottom: 4,
  },
  promoText: {
    color: '#00C853', // Texto verde vibrante
    fontWeight: 'bold',
    fontSize: 13,
    marginLeft: 6,
  },
  subtitle: {
    color: Colors.background,
    fontSize: SIZES.medium,
    marginTop: 8,
    opacity: 0.9,
  },
  form: {
    flex: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    padding: SIZES.padding * 1.5,
  },
  // Sin alto explicito el boton nativo de Apple NO SE VE (lo dice su propia
  // documentacion). 52 px para que quede a la par del de Google.
  appleButton: {
    width: '100%',
    height: 52,
    marginBottom: 12,
  },
  googleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    height: 52,
    borderRadius: SIZES.radius,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  googleButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  dividerText: {
    marginHorizontal: 10,
    color: Colors.textSecondary,
    fontSize: 12,
  },
  label: {
    fontSize: 14,
    color: Colors.text,
    marginBottom: 8,
    fontWeight: '600',
  },
  autoProfileNote: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 200, 83, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(0, 200, 83, 0.25)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  autoProfileNoteText: {
    flex: 1,
    fontSize: 12.5,
    color: '#0A7A3D',
    lineHeight: 17,
    fontWeight: '600',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background,
    borderRadius: SIZES.radius,
    borderWidth: 1,
    borderColor: Colors.border,
    height: 56,
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  inputIcon: {
    marginRight: 10,
  },
  countryPickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    height: '100%',
    borderRightWidth: 1,
    borderRightColor: Colors.border,
    marginRight: 12,
  },
  flag: {
    fontSize: 20,
    marginRight: 4,
  },
  prefix: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: Colors.text,
    height: '100%',
  },
  validationContainer: {
    backgroundColor: '#F8F9FA',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E9ECEF',
  },
  validationTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: Colors.textSecondary,
    marginBottom: 8,
  },
  validationItem: {
    fontSize: 12,
    marginBottom: 4,
  },
  valid: {
    color: '#10B981',
  },
  invalid: {
    color: '#EF4444',
  },
  forgotPassword: {
    alignSelf: 'flex-end',
    marginBottom: 16,
  },
  forgotPasswordText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  button: {
    marginTop: 8,
  },
  switchModeContainer: {
    alignItems: 'center',
    marginTop: 20,
  },
  switchModeText: {
    fontSize: 15,
    color: Colors.textSecondary,
  },
  switchModeLink: {
    color: Colors.primary,
    fontWeight: 'bold',
  },
  terms: {
    fontSize: 12,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginTop: 24,
    lineHeight: 18,
  },
  developerContainer: {
    alignItems: 'center',
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  developerText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: Colors.primary,
    letterSpacing: 1,
  },
});
